import { useEffect, useState } from 'react'
import { Link, useLocation } from "react-router-dom";
import ROUTES from '../../routes';
import api from './../../config/axiosInstance';
import { formatDate, handleErrors } from '../../utils/common';
import { showToast } from '../../utils/toast';

function CTAList() {
    const Swal = window.Swal;
    const location = useLocation();
    const type = location.pathname.includes('career') ? 'career' : 'contact-us';
    const title = type === 'career' ? 'Careers' : 'Contact Us';

    const [baseUrl, setBaseUrl] = useState("");
    const limit = 10;
    const [records, setRecords] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        const controller = new AbortController();
        const fetchRecords = async (page = 1, searchQuery = "") => {
            try {
                setLoading(true);
                const offset = (page - 1) * limit;
                const res = await api.get(
                    `/cta/list?type=${type}&limit=${limit}&offset=${offset}&search=${encodeURIComponent(searchQuery)}`,
                    { signal: controller.signal }
                );
                if (res.status === 200) {
                    setRecords(res.data.cta);
                    setBaseUrl(res.data.base_url);
                    const total = res.data.total || 0;
                    setTotalPages(Math.ceil(total / limit));
                }
            } catch (err) {
                console.log(err);
                if (err.name !== "CanceledError") handleErrors(err);
            } finally {
                setLoading(false);
            }
        };

        const delayDebounce = setTimeout(() => {
            fetchRecords(currentPage, search);
        }, 500);

        return () => {
            clearTimeout(delayDebounce);
            controller.abort();
        };
    }, [currentPage, search, type]);

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.delete(`/cta/${id}`);
                    if (res.status === 200) {
                        showToast('success', res.data.message, 'Success');
                        setRecords((prev) => prev.filter((r) => r._id !== id));
                    }
                } catch (err) {
                    handleErrors(err);
                }
            }
        });
    };

    const generatePagination = () => {
        const pages = [];
        const maxVisible = 5;
        const sidePages = 2;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > sidePages + 2) pages.push('...');
            const start = Math.max(2, currentPage - sidePages);
            const end = Math.min(totalPages - 1, currentPage + sidePages);
            for (let i = start; i <= end; i++) pages.push(i);
            if (currentPage < totalPages - sidePages - 1) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={type === 'career' ? ROUTES.CTA_CAREER : ROUTES.CTA_CONTACT_US}>{title}</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">{title} Proposals</h4>
                        </div>
                        <div className="card-body">

                            <div className="row mb-3">
                                <div className="col-lg-3 col-md-4">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by name, email, phone..."
                                        value={search}
                                        onChange={(e) => {
                                            setCurrentPage(1);
                                            setSearch(e.target.value);
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="table table-responsive-md">
                                        <thead>
                                            <tr>
                                                <th className="width80"><strong>#</strong></th>
                                                <th><strong>Name</strong></th>
                                                <th><strong>Email</strong></th>
                                                <th><strong>Phone</strong></th>
                                                {type === 'career' && <th><strong>Position</strong></th>}
                                                {type === 'career' && <th><strong>Resume</strong></th>}
                                                {type === 'contact-us' && <th><strong>Message</strong></th>}
                                                <th><strong>Created At</strong></th>
                                                <th><strong>Action</strong></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records && records.length > 0 ? records.map((record, index) => (
                                                <tr key={record._id || index}>
                                                    <td><strong>{(currentPage - 1) * limit + index + 1}</strong></td>
                                                    <td>{record.name}</td>
                                                    <td>{record.email}</td>
                                                    <td>{record.phone}</td>
                                                    {type === 'career' && <td>{record.position}</td>}
                                                    {type === 'career' && (
                                                        <td>
                                                            {record.resume ? (
                                                                <a
                                                                    href={record.resume.startsWith('http') ? record.resume : baseUrl + record.resume}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="btn btn-info btn-xs sharp"
                                                                >
                                                                    <i className="fa fa-download"></i>
                                                                </a>
                                                            ) : (
                                                                <span className="text-muted">No resume</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {type === 'contact-us' && (
                                                        <td style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {record.message}
                                                        </td>
                                                    )}
                                                    <td>{formatDate(record.t)}</td>
                                                    <td>
                                                        <div className="d-flex">
                                                            <a
                                                                href="#/"
                                                                className="btn btn-danger shadow btn-xs sharp"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleDelete(record._id);
                                                                }}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan={type === 'career' ? 8 : 7} className="text-center">No records found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <div className="card-footer">
                            <ul className="pagination pagination-gutter pagination-primary no-bg">
                                <li className={`page-item page-indicator ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); if (currentPage > 1) setCurrentPage(currentPage - 1); }}>
                                        <i className="la la-angle-left"></i>
                                    </a>
                                </li>
                                {generatePagination().map((page, index) =>
                                    page === '...' ? (
                                        <li className="page-item disabled" key={index}>
                                            <span className="page-link">...</span>
                                        </li>
                                    ) : (
                                        <li className={`page-item ${currentPage === page ? 'active' : ''}`} key={index}>
                                            <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); setCurrentPage(page); }}>{page}</a>
                                        </li>
                                    )
                                )}
                                <li className={`page-item page-indicator ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) setCurrentPage(currentPage + 1); }}>
                                        <i className="la la-angle-right"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CTAList;
